import React, { useState, useEffect, useCallback } from 'react'
import '../styles/Press.css'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import pressData from '../components/press/pressData';
import HorizontalScroller from '../components/press/HorizontalScroller';
import Papa from 'papaparse';
// import InstagramWidget from '../components/InstagramWidget';
// import PressArticle from '../components/press/PressArticle';

const NY_POST_ARTICLE_URL = "https://nypost.com/2024/02/02/entertainment/grammys-2024-inside-the-insanely-exclusive-pop-star-gift-bag/";
const NY_POST_ARTICLE_IMAGE = "https://nypost.com/wp-content/uploads/sites/2/2024/02/grammy-gift-bag-everybody-gets-75836803.jpg?resize=2048,1536&quality=75&strip=all";

// CSV to JavaScript data converter using PapaParse
const convertCsvToPressData = async () => {
  try {
    const response = await fetch('/pressData.csv')
    const csvText = await response.text()
    
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        transform: (value, field) => {
          // Trim values
          return value.trim()
        },
        complete: (results) => {
          const pressData = results.data.map(row => {
            const convertedRow = {}
            
            // Convert id to number
            if (row.id) {
              convertedRow.id = parseInt(row.id, 10)
            }
            
            // Convert title, date, summary (keep as strings)
            if (row.title) convertedRow.title = row.title
            if (row.date) convertedRow.date = row.date
            if (row.summary) convertedRow.summary = row.summary
            
            // Convert source, originalUrl (optional fields)
            if (row.source) convertedRow.source = row.source
            if (row.originalUrl) convertedRow.originalUrl = row.originalUrl
            
            // Convert type
            if (row.type) convertedRow.type = row.type
            
            // Convert img - handle pipe-separated multiple images
            if (row.img) {
              if (row.img.includes(' | ')) {
                convertedRow.img = row.img.split(' | ').map(img => img.trim()).filter(img => img !== '')
              } else {
                convertedRow.img = row.img
              }
            }
            
            return convertedRow
          }).filter(row => row.id !== undefined) // Remove rows without id
          
          resolve(pressData)
        },
        error: (error) => {
          reject(error)
        }
      })
    })
  } catch (error) {
    console.error('Error converting CSV to press data:', error)
    return []
  }
}

const Press = () => {
  const [fullscreenImage, setFullscreenImage] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [csvPressData, setCsvPressData] = useState(null)

  // Convert CSV on component mount
  useEffect(() => {
    convertCsvToPressData().then(data => {
      setCsvPressData(data)
      console.log('Converted press data from CSV:', data)
    })
  }, [])

  // Use CSV data if available, otherwise fall back to imported pressData
  const displayData = csvPressData || pressData

  const navToPage = data => {
    window.open(data.originalUrl, "_blank")
  }

  const openFullscreen = (data) => {
    setFullscreenImage(data)
    setCurrentImageIndex(0)
  }

  const closeFullscreen = () => {
    setFullscreenImage(null)
    setCurrentImageIndex(0)
  }

  const nextImage = (e) => {
    e.stopPropagation()
    if (fullscreenImage && Array.isArray(fullscreenImage.img) && currentImageIndex < fullscreenImage.img.length - 1) {
      setCurrentImageIndex((prev) => prev + 1)
    }
  }

  const prevImage = (e) => {
    e.stopPropagation()
    if (fullscreenImage && Array.isArray(fullscreenImage.img) && currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1)
    }
  }

  const getCurrentImage = () => {
    if (!fullscreenImage) return null
    if (Array.isArray(fullscreenImage.img)) {
      return fullscreenImage.img[currentImageIndex]
    }
    return fullscreenImage.img
  }

  const hasMultipleImages = useCallback(() => {
    return fullscreenImage && Array.isArray(fullscreenImage.img) && fullscreenImage.img.length > 1
  }, [fullscreenImage])

  // Keyboard navigation for fullscreen images
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!fullscreenImage || !hasMultipleImages()) return

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          if (currentImageIndex > 0) {
            setCurrentImageIndex(prev => prev - 1)
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (currentImageIndex < fullscreenImage.img.length - 1) {
            setCurrentImageIndex(prev => prev + 1)
          }
          break
        case 'Escape':
          closeFullscreen()
          break
        default:
          break
      }
    }

    if (fullscreenImage) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullscreenImage, currentImageIndex, hasMultipleImages])

  return (
    <div className='minHeight100'>
      <div className='hero-section'>
        <h2 className='pagetitle'>PRESS</h2>
        {/* <p className='intro-text'>
          Highlights from major media outlets covering Gift Bags by Rachael's prestigious events and celebrity gifting experiences
        </p> */}
      </div>
      
      <div className='featured-coverage-section section-container'>
        <div className='coverage-content'>
          <div className='coverage-description'>
            <div className='description-card'>
              <h4 className='description-title'>Industry Recognition</h4>
              <p className='description-text'>
                Rachael's luxury gift bags have been featured at top events like Essence Black Women in Hollywood's Luncheon Oscar Week,
                The SAG Awards, and GRAMMYs, with press coverage in People, Fortune, LA Confidential, and more.
              </p>
            </div>
            <div className='description-card'>
              <h4 className='description-title'>Celebrity Connections</h4>
              <p className='description-text'>
                From Oscar Week at the Four Seasons to the TIME 100 Gala, her #giftbagsbyRachael deliver curated luxury, 
                connecting A-list talent with premium brands at entertainment's most prestigious events.
              </p>
            </div>
          </div>
          <div className='video-container'>
            <div className='video-wrapper'>
              <h4 className='description-title'>Featured Coverage</h4>
              <a 
                href={NY_POST_ARTICLE_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className='article-preview-card'
              >
                <div className='article-preview-image-container'>
                  <LazyLoadImage
                    src={NY_POST_ARTICLE_IMAGE}
                    alt="Grammys 2024 gift bag article"
                    className='article-preview-image'
                  />
                </div>
                <div className='article-content'>
                  <img src={"/pressImgs/ny-post-logo.png"} alt="New York Post Logo" className='article-preview-source-logo' />
                  <LazyLoadImage
                    src={"/pressImgs/ny-post-headline.png"}
                    alt="Grammys 2024 gift bag article"
                    className='article-preview-title-image'
                  />
                  {/* <h5 className='article-title'>Pop-superstar swag: Inside the exclusive <br/>Grammys 2024 gift bag</h5> */}
                  <p className='article-date'>February 2, 2024</p>
                  <div className='article-preview-link'>
                    Read Full Article →
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className='press-articles-section'>
        <div className='section-container'>
          <h2 className='section-title'>PRESS ARTICLES</h2>
          <div className='articles-intro'>
            <p className='intro-text'>
              Comprehensive coverage from leading media outlets showcasing Gift Bags by Rachael's impact 
              on Hollywood's entertainment industry and celebrity culture
            </p>
          </div>
            <HorizontalScroller
              content={
                <div className='articles-grid'>
                  {
                    displayData.map(data =>
                      data.type==="long" ?
                      <button key={data.id} className='article-card article-card-long' onClick={() => navToPage(data)}>
                        <div className='article-image-container'>
                          <LazyLoadImage
                            className='article-image article-image-long'
                            src={data.img} 
                            alt={data.title}
                          />
                          <div className='article-overlay'>
                            <span className='read-more-text'>Read Full Article</span>
                          </div>
                        </div>
                        <div className='article-content'>
                          <div className='article-source'>{data.source}</div>
                          <h4 className='article-title'>{data.title}</h4>
                          <p className='article-date'>{data.date}</p>
                          <p className='article-summary'>{data.summary}</p>
                        </div>
                      </button> :
                      <div key={data.id} className='article-card article-card-short' onClick={() => data.type !== 'mixed' && openFullscreen(data)}>
                        <div className='article-image-container'>
                          <img className='article-image article-image-short' src={Array.isArray(data.img) ? data.img[0] : data.img} alt={data.title}/>
                          <div className='article-overlay'>
                            <button className='view-image-text' onClick={() => openFullscreen(data)}>View Image{Array.isArray(data.img) && data.img.length > 1 ? 's' : ''}</button>
                            {
                              data.type === 'mixed' &&
                              <button className='read-more-text' onClick={() => navToPage(data)}>Read Full Article</button>
                            }
                          </div>
                        </div>
                        <div className='article-content'>
                          { data.source && data.source !== '' && <div className='article-source'>{data.source}</div> }
                          <h4 className='article-title'>{data.title}</h4>
                          <p className='article-date'>{data.date}</p>
                          <p className='article-summary'>{data.summary}</p>
                        </div>
                      </div>
                    )
                  }
                </div>
              }
            />
        </div>
      </div>

      {/* Fullscreen overlay for short press cards */}
      {fullscreenImage && (
        <div className="fullscreen-overlay" onClick={closeFullscreen}>
          <div>
            <button className="fullscreen-close" onClick={closeFullscreen}>×</button>
            
            {/* Navigation arrows for multiple images */}
            {hasMultipleImages() && (
              <>
                {currentImageIndex > 0 && (
                  <button className="fullscreen-nav fullscreen-nav-prev" onClick={prevImage}>
                    ‹
                  </button>
                )}
                {currentImageIndex < fullscreenImage.img.length - 1 && (
                  <button className="fullscreen-nav fullscreen-nav-next" onClick={nextImage}>
                    ›
                  </button>
                )}
              </>
            )}
            
            <img 
              src={getCurrentImage()} 
              alt={fullscreenImage.title} 
              className="fullscreen-image"
            />
            
            {/* Image counter for multiple images */}
            {hasMultipleImages() && (
              <div className="fullscreen-counter">
                {currentImageIndex + 1} / {fullscreenImage.img.length}
              </div>
            )}
            
            <div className="fullscreen-caption">
              <h4>{fullscreenImage.title}</h4>
              <p>{fullscreenImage.date}</p>
              <p>{fullscreenImage.summary}</p>
            </div>
            
            {/* Keyboard navigation hint for multiple images */}
            {hasMultipleImages() && (
              <div className="fullscreen-keyboard-hint">
                Use ← → arrow keys or click arrows to navigate
              </div>
            )}
          </div>
        </div>
      )}

      <div className='social-media-section'>
        <div className='section-container'>
          <h2 className='section-title'>SOCIAL MEDIA</h2>
          <div className='social-intro'>
            <p className='intro-text'>
              Follow Gift Bags by Rachael's journey through social media, featuring behind-the-scenes moments, 
              celebrity interactions, and exclusive event coverage
            </p>
          </div>
          
          {/* <div className='social-widgets'>
            <div className='widget-card'>
              <h4 className='widget-title'>Instagram Feed</h4>
              <p className='widget-description'>Latest posts and behind-the-scenes content</p>
              <div className='widget-container'>
                <InstagramWidget />
              </div>
            </div>
            
            <div className='widget-card'>
              <h4 className='widget-title'>Alternative Instagram Feed</h4>
              <p className='widget-description'>Premium feed option with enhanced features</p>
              <div className='widget-container'>
                <iframe src="//lightwidget.com/widgets/bdfd9bc1bf3e5671ab80d8297cbc9132.html" 
                  allowtransparency="true" className='lightwidget-widget'></iframe>
              </div>
            </div>
          </div> */}

          <div className='tiktok-content'>
            <div className='tiktok-stats'>
              <h3 className='tiktok-title'>TikTok Highlights</h3>
              <div className='stats-row'>
                <div className='stat-box'>
                  <h1 className='stat-big-number'>34K</h1>
                  <h4 className='stat-big-label'>FOLLOWERS</h4>
                </div>
                <div className='stat-box'>
                  <h1 className='stat-big-number'>914K</h1>
                  <h4 className='stat-big-label'>LIKES</h4>
                </div>
              </div>
              <p className='tiktok-description'>
                Behind-the-scenes content showcasing celebrity gifting experiences and exclusive event access
              </p>
            </div>
            <div className='tiktok-embed'>
              <iframe title='Embedded Tiktok'
                src="https://www.tiktok.com/embed/7475867194690194718"
                className='tiktok-iframe'
                allow="encrypted-media"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Press